/**
 * Visualizer Store (Zustand)
 *
 * Per-panel visualizer mode configuration. Each IO panel (input/output)
 * gets its own mode (raw/tree/table/diff/diagram/markdown/railroad)
 * and composable modifiers (syntax highlighting, line numbers, word wrap).
 *
 * Replaces the old `visualizerTabs` / `activeVisualizerTab` in useAppStore.
 * Visualizers are now panel modifiers — not pipeline steps.
 *
 * @see docs/Reaserch/BackendAdvocacyDesignDoc.md §3
 */

import { create } from 'zustand';

import {
  DEFAULT_PANEL_CONFIG as _DEFAULT_PANEL_CONFIG,
  DEFAULT_VISUALIZER_MODIFIERS as _DEFAULT_VISUALIZER_MODIFIERS,
} from '@/lib/storeConstants';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/** Available visualizer modes — each maps to a renderer in VisualizerModeRegistry. */
export type VisualizerMode =
  | 'raw'
  | 'tree'
  | 'table'
  | 'structured-table'
  | 'hex'
  | 'diff'
  | 'diagram'
  | 'markdown'
  | 'railroad';

/** Composable display modifiers applied on top of any mode. */
export interface VisualizerModifiers {
  syntaxHighlighting: boolean;
  lineNumbers: boolean;
  wordWrap: boolean;
}

/** Per-panel visualizer configuration. */
export interface PanelVisualizerConfig {
  mode: VisualizerMode;
  modifiers: VisualizerModifiers;
  modeSpecificState: Record<string, unknown>;
}

/** Which panel to target. */
export type PanelRole = 'input' | 'output';

// ─────────────────────────────────────────────────────────────────────────────
// Defaults (re-exported from storeConstants for backward compatibility)
// ─────────────────────────────────────────────────────────────────────────────

export const DEFAULT_VISUALIZER_MODIFIERS = _DEFAULT_VISUALIZER_MODIFIERS;
export const DEFAULT_PANEL_CONFIG = _DEFAULT_PANEL_CONFIG;

// ─────────────────────────────────────────────────────────────────────────────
// Store interface
// ─────────────────────────────────────────────────────────────────────────────

/** Max user-added modes in the quick-switch bar (+ permanent 'raw' = MAX+1 total). */
const MAX_USER_ACTIVE_MODES = 4;

interface VisualizerStoreState {
  inputPanel: PanelVisualizerConfig;
  outputPanel: PanelVisualizerConfig;

  /** Per-panel list of user-activated modes for quick-switch. 'raw' is always first. */
  inputActiveModes: VisualizerMode[];
  outputActiveModes: VisualizerMode[];

  /** Set the display mode for a panel. */
  setMode: (panel: PanelRole, mode: VisualizerMode) => void;
  /** Toggle a boolean modifier on a panel. */
  toggleModifier: (panel: PanelRole, modifier: keyof VisualizerModifiers) => void;
  /** Set a modifier explicitly. */
  setModifier: (panel: PanelRole, modifier: keyof VisualizerModifiers, value: boolean) => void;
  /** Replace mode-specific state for a panel (tree expand state, search, etc.). */
  setModeSpecificState: (panel: PanelRole, state: Record<string, unknown>) => void;
  /** Merge into mode-specific state for a panel. */
  updateModeSpecificState: (panel: PanelRole, patch: Record<string, unknown>) => void;
  /** Get config for a panel. */
  getPanel: (panel: PanelRole) => PanelVisualizerConfig;
  /** Add a mode to the quick-switch bar. No-op if already present or at capacity. */
  addActiveMode: (panel: PanelRole, mode: VisualizerMode) => void;
  /** Remove a mode from the quick-switch bar. 'raw' cannot be removed. */
  removeActiveMode: (panel: PanelRole, mode: VisualizerMode) => void;
  /** Reset both panels to defaults. */
  reset: () => void;
  /** Load full config for both panels (e.g. from tab restore). */
  loadPanelConfigs: (input: PanelVisualizerConfig, output: PanelVisualizerConfig) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function panelKey(panel: PanelRole): 'inputPanel' | 'outputPanel' {
  return panel === 'input' ? 'inputPanel' : 'outputPanel';
}

function activeModesKey(panel: PanelRole): 'inputActiveModes' | 'outputActiveModes' {
  return panel === 'input' ? 'inputActiveModes' : 'outputActiveModes';
}

// ─────────────────────────────────────────────────────────────────────────────
// Store
// ─────────────────────────────────────────────────────────────────────────────

export const useVisualizerStore = create<VisualizerStoreState>((set, get) => ({
  inputPanel: { ...DEFAULT_PANEL_CONFIG, modifiers: { ...DEFAULT_VISUALIZER_MODIFIERS } },
  outputPanel: { ...DEFAULT_PANEL_CONFIG, modifiers: { ...DEFAULT_VISUALIZER_MODIFIERS } },
  inputActiveModes: ['raw'] as VisualizerMode[],
  outputActiveModes: ['raw'] as VisualizerMode[],

  setMode: (panel, mode) => {
    const key = panelKey(panel);
    set((state) => ({
      [key]: { ...state[key], mode, modeSpecificState: {} },
    }));
  },

  toggleModifier: (panel, modifier) => {
    const key = panelKey(panel);
    set((state) => ({
      [key]: {
        ...state[key],
        modifiers: {
          ...state[key].modifiers,
          [modifier]: !state[key].modifiers[modifier],
        },
      },
    }));
  },

  setModifier: (panel, modifier, value) => {
    const key = panelKey(panel);
    set((state) => ({
      [key]: {
        ...state[key],
        modifiers: {
          ...state[key].modifiers,
          [modifier]: value,
        },
      },
    }));
  },

  setModeSpecificState: (panel, modeState) => {
    const key = panelKey(panel);
    set((state) => ({
      [key]: { ...state[key], modeSpecificState: modeState },
    }));
  },

  updateModeSpecificState: (panel, patch) => {
    const key = panelKey(panel);
    set((state) => ({
      [key]: {
        ...state[key],
        modeSpecificState: { ...state[key].modeSpecificState, ...patch },
      },
    }));
  },

  getPanel: (panel) => {
    const key = panelKey(panel);
    return get()[key];
  },

  addActiveMode: (panel, mode) => {
    const key = activeModesKey(panel);
    set((state) => {
      const current = state[key];
      if (current.includes(mode)) return state;
      // 'raw' is always present; user-added = total - 1 (raw)
      const userCount = current.length - 1;
      if (userCount >= MAX_USER_ACTIVE_MODES) return state;
      return { [key]: [...current, mode] };
    });
  },

  removeActiveMode: (panel, mode) => {
    if (mode === 'raw') return; // raw is permanent
    const key = activeModesKey(panel);
    set((state) => ({
      [key]: state[key].filter((m) => m !== mode),
    }));
  },

  reset: () => {
    set({
      inputPanel: { ...DEFAULT_PANEL_CONFIG, modifiers: { ...DEFAULT_VISUALIZER_MODIFIERS } },
      outputPanel: { ...DEFAULT_PANEL_CONFIG, modifiers: { ...DEFAULT_VISUALIZER_MODIFIERS } },
      inputActiveModes: ['raw'] as VisualizerMode[],
      outputActiveModes: ['raw'] as VisualizerMode[],
    });
  },

  loadPanelConfigs: (input, output) => {
    set({
      inputPanel: { ...input },
      outputPanel: { ...output },
    });
  },
}));
